import * as React from 'react';
import {
  line as lineCls,
  circle as circleCls,
  card as cardCls,
  textGroup as textGroupCls,
  textLine as textLineCls,
} from './Skeleton.css';

export type SkeletonShape = 'text' | 'line' | 'circle' | 'card';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Placeholder shape (M17 — this is a SHAPE enum, deliberately named
   * `shape` rather than `as`, which across the DS means "the rendered
   * element"). Default `line`.
   */
  shape?: SkeletonShape;
  width?: number | string;
  height?: number | string;
  /** For `shape='text'` — number of stacked lines. Last line is 60% width. */
  lines?: number;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

function sizeStyle(width?: number | string, height?: number | string): React.CSSProperties {
  return {
    width: width === undefined ? undefined : typeof width === 'number' ? `${width}px` : width,
    height: height === undefined ? undefined : typeof height === 'number' ? `${height}px` : height,
  };
}

/**
 * Skeleton — content placeholder for loading states. Four shapes:
 *   - `line` (default) — single horizontal bar; pass `width`/`height`
 *   - `text` — multiple stacked lines (`lines`, default 3) with the
 *     last line at 60% width to mimic a paragraph
 *   - `circle` — round avatar / icon placeholder
 *   - `card` — block placeholder for cards / images
 *
 * Every shape honours `width` / `height` (the `text` group sizes the
 * stack container). Each sets `role="status"` + `aria-label="Loading"`
 * so screen readers announce the loading state — `role="status"`
 * already implies an `aria-live="polite"` region, so no explicit
 * `aria-live` is set (L13). The pulse animation is opacity-only and is
 * disabled under `prefers-reduced-motion` (in `Skeleton.css.ts`).
 */
export function Skeleton({
  shape = 'line',
  width,
  height,
  lines = 3,
  style,
  ref,
  className,
  ...rest
}: SkeletonProps) {
  const combined = { ...sizeStyle(width, height), ...style };

  if (shape === 'text') {
    const cls = [textGroupCls, className].filter(Boolean).join(' ');
    return (
      <div ref={ref} className={cls} role="status" aria-label="Loading" style={combined} {...rest}>
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className={textLineCls({ last: i === lines - 1 })} />
        ))}
      </div>
    );
  }
  if (shape === 'circle') {
    const cls = [circleCls, className].filter(Boolean).join(' ');
    return (
      <div
        ref={ref}
        className={cls}
        role="status"
        aria-label="Loading"
        style={combined}
        {...rest}
      />
    );
  }
  if (shape === 'card') {
    const cls = [cardCls, className].filter(Boolean).join(' ');
    return (
      <div
        ref={ref}
        className={cls}
        role="status"
        aria-label="Loading"
        style={combined}
        {...rest}
      />
    );
  }
  const cls = [lineCls, className].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={cls} role="status" aria-label="Loading" style={combined} {...rest} />
  );
}
Skeleton.displayName = 'Skeleton';
